import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { PageMeta } from "../components/PageMeta";
import { PageHero } from "../components/PageHero";
import { HeroVideo } from "../components/heroes/HeroVideo";
import { Reveal } from "../components/Reveal";
import { CtaBand } from "../components/CtaBand";
import { AUDIENCES, MEMBERSHIPS, TESTIMONIALS } from "../data/content";
import { TestimonialCard } from "../components/TestimonialCard";
import { Pic } from "../components/Pic";
import { ClientLogoSlider } from "../components/ClientLogoSlider";
import { InquiryCTA } from "../components/PlanEventModal";

const MembershipBadge = ({ slug, m }) => (
  <a href={m.url} target="_blank" rel="noopener noreferrer" data-testid={`membership-${slug}`} className="group mt-4 flex items-center gap-4 rounded-2xl border border-black/10 bg-white p-4 transition-[border-color,box-shadow] duration-300 hover:border-en-teal hover:shadow-[0_12px_30px_-20px_rgba(0,0,0,0.4)] sm:gap-5 sm:p-5">
    <img src={m.logo} alt={`${m.short} logo`} loading="lazy" width={m.w} height={m.h} className="h-8 w-auto shrink-0 lg:h-11" />
    <span className="min-w-0 flex-1">
      <span className="eyebrow block text-[10px] sm:text-[11px]">Member</span>
      <span className="mt-0.5 block text-sm leading-snug text-black/80 sm:text-base">Proud member of the {m.org}</span>
    </span>
    <ExternalLink aria-hidden className="h-4 w-4 shrink-0 text-black/40 transition-colors group-hover:text-en-teal" />
  </a>
);

export default function WhoWeServe() {
  return (
    <>
      <PageMeta title="Who We Serve" description="Entertainment Nation hosts live game experiences for corporate teams, country clubs, communities, charities and private events." />
      <PageHero testId="who-we-serve-hero" variant="audiences" eyebrow="Who we serve" titleFx="fadeup" title="Real people. Real crowds. Real connections." intro="From leadership development and large-scale corporate conferences to private parties, country clubs, hotels, restaurants and brand activations, our live hosted games are designed to get guests to actually participate." visual={<HeroVideo />}
        actions={<><InquiryCTA label="Book Here" icon data-testid="who-we-serve-hero-cta" className="btn-primary !px-8 !py-4" /><Link to="/experiences" data-testid="who-we-serve-hero-experiences-link" className="btn-outline !px-8 !py-4">Browse game packages</Link></>} />

      <div className="bg-white">
        {AUDIENCES.map((a, i) => (
          <section key={a.slug} id={a.slug} data-testid={`audience-section-${a.slug}`} className={`scroll-mt-24 py-16 lg:py-24 bg-white ${i ? "border-t border-black/10" : ""}`}>
            <div className={`mx-auto grid max-w-7xl items-center gap-10 px-5 sm:px-8 lg:grid-cols-12 lg:gap-16`}>
              <Reveal delay={0.1} className={`lg:col-span-6 ${i % 2 ? "lg:order-2" : "lg:order-1"}`}>
                <p className="eyebrow mb-3">0{i + 1}</p>
                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">{a.title}</h2>
                <ul className="mt-6 space-y-3">
                  {a.lines.map((l) => (
                    <li key={l} className="flex gap-3 text-base leading-relaxed text-black/75">
                      <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-en-yellow" />{l}
                    </li>
                  ))}
                </ul>
                {a.slug === "corporate" && (
                  <div className="mt-6 grid gap-4 sm:grid-cols-2" data-testid="corporate-quotes">
                    {["robyn", "libby"].map((id, i) => { const t = TESTIMONIALS.find((x) => x.id === id); return <TestimonialCard key={id} t={t} index={`corporate-${i}`} />; })}
                  </div>
                )}
                {MEMBERSHIPS[a.slug] && <MembershipBadge slug={a.slug} m={MEMBERSHIPS[a.slug]} />}
                <div className="mt-8 flex flex-wrap items-center gap-4">
                  <InquiryCTA label="Book Here" eventType={a.eventType} icon data-testid={`audience-cta-${a.slug}`} className="btn-outline" />
                  <Link to="/experiences" data-testid={`audience-experiences-${a.slug}`} className="font-gothic uppercase tracking-wider text-xs text-en-teal underline-offset-4 hover:underline">Browse game packages</Link>
                </div>
              </Reveal>
              <Reveal className={`lg:col-span-6 ${i % 2 ? "lg:order-1" : "lg:order-2"}`}>
                <div className="overflow-hidden rounded-2xl border border-black/10 shadow-[0_20px_50px_-30px_rgba(0,0,0,0.4)]">
                  <Pic src={a.image} alt={a.alt} sizes="(min-width: 1024px) 50vw, 100vw" className="aspect-[16/10] w-full object-cover transition-transform duration-700 hover:scale-105" />
                </div>
              </Reveal>
            </div>
            {a.logoFiles && (
              <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <p className="font-gothic mt-12 text-center uppercase tracking-[0.18em] text-[11px] text-black/50 lg:mt-16">Some of the {a.title.toLowerCase()} we've worked with</p>
                <ClientLogoSlider files={a.logoFiles} slug={a.slug} />
              </div>
            )}
          </section>
        ))}
      </div>

      <CtaBand testId="who-we-serve-cta" />
    </>
  );
}
